import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import RGRGallery from './RGRGallery';

const SHADOW = '4px 4px 0px #1e1e1e';

const RGR_RED = '#C8102E';
const PRESSTO_PURPLE = '#5C4FB8';

function PrimaryButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <MuiButton
      variant="contained"
      onClick={onClick}
      sx={{
        backgroundColor: '#2C2C2C',
        color: '#FFFFFF',
        fontFamily: 'Montserrat, Inter, sans-serif',
        fontWeight: 700,
        fontSize: '1rem',
        textTransform: 'none',
        borderRadius: '6px',
        border: '2px solid #1e1e1e',
        boxShadow: SHADOW,
        px: 4,
        py: 1.5,
        '&:hover': {
          backgroundColor: '#1e1e1e',
          boxShadow: '2px 2px 0px #1e1e1e',
          transform: 'translate(2px, 2px)',
        },
        transition: 'all 100ms ease-in-out',
      }}
    >
      {children}
    </MuiButton>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function Hero() {
  return (
    <Box
      sx={{
        backgroundColor: '#F2EEE5',
        py: { xs: 10, md: 14 },
        px: { xs: 3, md: 6 },
        position: 'relative',
        overflow: 'hidden',
        borderBottom: '2px solid #1e1e1e',
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center', position: 'relative' }}>
        {/* P stamp badge */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 96,
            height: 96,
            backgroundColor: '#F4A6B8',
            borderRadius: '50%',
            border: '2px solid #1e1e1e',
            boxShadow: SHADOW,
            mb: 4,
            position: 'relative',
            transform: 'rotate(-8deg)',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Montserrat, Inter, sans-serif',
              fontWeight: 900,
              fontSize: '2.5rem',
              color: PRESSTO_PURPLE,
              fontStyle: 'italic',
            }}
          >
            P
          </Typography>
        </Box>

        <Typography
          variant="h1"
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontSize: { xs: '2.25rem', md: '4rem' },
            fontWeight: 900,
            lineHeight: 1.05,
            mb: 3,
            color: '#1e1e1e',
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
          }}
        >
          Structured Writing for{' '}
          <Box component="span" sx={{ color: RGR_RED }}>
            Really Great Reading
          </Box>
        </Typography>

        <Typography
          sx={{
            fontFamily: 'Lato, Inter, sans-serif',
            fontSize: { xs: '1.05rem', md: '1.2rem' },
            color: '#444',
            lineHeight: 1.7,
            maxWidth: 680,
            mx: 'auto',
          }}
        >
          Pressto delivers structured writing activities aligned to Really Great Reading — complete with graphic organizers, scaffolds, target spelling patterns, and real-time feedback from our AI Writing Buddy.
        </Typography>
      </Box>
    </Box>
  );
}

// ─── CTA ───────────────────────────────────────────────────────────────────────
function FinalCTA() {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        backgroundColor: '#2C2C2C',
        borderTop: '2px solid #1e1e1e',
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        textAlign: 'center',
      }}
    >
      <Box sx={{ maxWidth: 700, mx: 'auto' }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 800,
            color: '#FFFFFF',
            mb: 2,
          }}
        >
          Bring Writing Practice to Your RGR Classroom
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lato, Inter, sans-serif',
            fontSize: { xs: '1rem', md: '1.15rem' },
            color: 'rgba(255,255,255,0.7)',
            lineHeight: 1.7,
            mb: 5,
          }}
        >
          Free to start. Activities for every RGR program. Students apply target patterns in their own writing — with scaffolds and AI feedback built in.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <PrimaryButton onClick={() => navigate('/register')}>
            Get Started Free
          </PrimaryButton>
          <MuiButton
            variant="outlined"
            onClick={() => navigate('/contact')}
            sx={{
              borderColor: '#FFFFFF',
              border: '2px solid #FFFFFF',
              color: '#FFFFFF',
              fontFamily: 'Montserrat, Inter, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: '4px 4px 0px rgba(255,255,255,0.3)',
              px: 5,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderColor: '#FFFFFF',
                boxShadow: '2px 2px 0px rgba(255,255,255,0.3)',
                transform: 'translate(2px, 2px)',
              },
              transition: 'all 100ms ease-in-out',
            }}
          >
            Contact Sales
          </MuiButton>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Rationale: Why writing pairs with RGR ────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function WhyWriting() {
  const PILLARS = [
    {
      number: '01',
      title: 'Encoding cements decoding',
      desc: 'When students write words with the patterns they\'ve been decoding, the neural connection sticks. The research is clear: reading and writing share the same orthographic mapping.',
      accent: '#FFE082',
    },
    {
      number: '02',
      title: 'Writing reveals what reading hides',
      desc: 'A student can guess their way through a passage. They can\'t guess their way through a sentence they have to produce. Writing surfaces gaps that running records miss.',
      accent: PRESSTO_PURPLE,
      light: true,
    },
    {
      number: '03',
      title: 'Phonics finally has a destination',
      desc: 'Hours of phoneme practice deserve more than a worksheet. RGR + Pressto gives every spelling pattern a writing context, every decoded word a sentence to live in.',
      accent: RGR_RED,
      light: true,
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        borderBottom: '2px solid #1e1e1e',
      }}
    >
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 800,
              fontSize: '0.8rem',
              color: RGR_RED,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              mb: 2,
            }}
          >
            The strategic fit
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 900,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              color: '#1e1e1e',
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              maxWidth: 720,
              mx: 'auto',
            }}
          >
            Reading instruction is finally a science. Writing is the missing half.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
          }}
        >
          {PILLARS.map((p) => (
            <Box
              key={p.number}
              sx={{
                p: { xs: 3, md: 3.5 },
                borderRadius: '8px',
                border: '2px solid #1e1e1e',
                boxShadow: SHADOW,
                backgroundColor: p.accent,
                color: p.light ? '#FFFFFF' : '#1e1e1e',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 900,
                  fontSize: '2.5rem',
                  lineHeight: 1,
                  mb: 2,
                  opacity: 0.85,
                }}
              >
                {p.number}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 800,
                  fontSize: '1.2rem',
                  lineHeight: 1.2,
                  mb: 1.5,
                  letterSpacing: '-0.01em',
                }}
              >
                {p.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                  opacity: p.light ? 0.95 : 0.85,
                }}
              >
                {p.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function RGRLandingPage() {
  return (
    <LandingLayout>
      <RGRGallery />
      <FinalCTA />
    </LandingLayout>
  );
}
